// MobileCart.js
import React, { useState, useEffect } from "react";
import { Box, Drawer, Typography, IconButton, Button } from "@mui/material";
import DOMPurify from "dompurify";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { useTranslation } from "react-i18next";
import { useDiscountManager } from "../hooks/useDiscountManager";
import CustomMealPopUp from "./customMealPopUp";
import { cartItemShowsMealBreakdown } from "../utils/bundleUtils";
import useFetchProducts from "../hooks/useFetchProducts";

const MobileCart = ({
  cart,
  updateQty,
  showCartMenu,
  toggleCartMenu,
  removeFromCart,
  updateCustomMeal,
  isMobile,
}) => {
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();
  const [showDiscount, setShowDiscount] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [openCustomMealPopup, setOpenCustomMealPopup] = useState(false);
  const { products, loading } = useFetchProducts();

  const sanitizeInput = (input) => {
    if (typeof input === "string") {
      return DOMPurify.sanitize(input.trim());
    }
    return input;
  };
  const handleClose = () => {
    setErrors((prev) => ({ ...prev, message: "" }));
    toggleCartMenu();
  };
  const validateMinItems = () => {
    const totalQty = cart.reduce((sum, item) => sum + item.qty, 0);
    const hasBundle = cart.some((item) => item.baseBundleId !== undefined);

    if (totalQty <= 2 && !hasBundle) {
      setErrors({ ...errors, message: t("error_minItemsNotMet") });
      return false;
    }
    setErrors((prev) => ({ ...prev, message: "" }));
    return true;
  };
  const { appliedDiscount } = useDiscountManager();

  useEffect(() => {
    if (appliedDiscount !== null) {
      setTimeout(() => setShowDiscount(true), 50);
    } else {
      setShowDiscount(false);
    }
  }, [appliedDiscount]);

  const calculateDiscountedTotal = () => {
    const discountValue = Number(appliedDiscount?.value || 0);
    const mealsTotal = cart.reduce(
      (sum, item) => sum + (item.baseBundleId ? 0 : item.price * item.qty),
      0
    );
    const bundlesTotal = cart.reduce(
      (sum, item) => sum + (item.baseBundleId ? item.price * item.qty : 0),
      0
    );
    const discountedMealsTotal = mealsTotal * (1 - discountValue / 100);
    const finalTotal = discountedMealsTotal + bundlesTotal;
    return isNaN(finalTotal) ? "N/A" : finalTotal.toFixed(2) + " ₪";
  };

  const calculateDiscountAmount = () => {
    const discountValue = Number(appliedDiscount?.value || 0);
    const mealsTotal = cart.reduce(
      (sum, item) => sum + (item.baseBundleId ? 0 : item.price * item.qty),
      0
    );
    const discountAmount = mealsTotal * (discountValue / 100);
    return isNaN(discountAmount)
      ? "N/A"
      : "-" + discountAmount.toFixed(2) + " ₪";
  };

  return (
    <>
      <Drawer
        anchor={isMobile ? "bottom" : "right"}
        open={showCartMenu}
        onClose={handleClose}
      >
        <Box
          sx={{
            height: isMobile ? "80vh" : "100%",
            width: isMobile ? "100%" : "min(420px, 45vw)",
            background:
              "linear-gradient(180deg, #f3faf5 0%, #ffffff 35%, #f7fcf9 100%)",
            display: "flex",
            flexDirection: "column",
            fontFamily: "Tajawal, Poppins, sans-serif",
            p: 2,
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
            <Typography
              sx={{
                fontSize: "1.45rem",
                fontWeight: 700,
                color: "#0f4d28",
                fontFamily: "Tajawal",
                pr: 2,
              }}
            >
              {t("cart")}
            </Typography>
          </Box>

          {/* Cart Items */}
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {cart.length === 0 ? (
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <Typography
                  textAlign="center"
                  color="text.secondary"
                  sx={{
                    fontSize: "clamp(1rem, 1vw, 1.5rem)",
                    fontFamily: "Tajawal",
                    fontWeight: "bold",
                    pt: 10,
                    pb: 12,
                  }}
                >
                  {t("emptyCart")}
                </Typography>

                <Button
                  variant="contained"
                  onClick={() => {
                    window.location.href = "/Order";
                  }}
                  className="dc-btn-soft"
                  sx={{
                    fontSize: "clamp(1rem, 1vw, 1.5rem)",
                    fontWeight: 700,
                    width: "55%",
                    borderRadius: 3,
                    py: 1.2,
                  }}
                >
                  {t("backToOrdering")}
                </Button>
              </Box>
            ) : (
              <>
                {cart.map((item) => (
                  <Box
                    key={sanitizeInput(item.id)}
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: 3,
                      mb: 1.5,
                      p: 1.75,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                      border: "1px solid rgba(27, 122, 61, 0.1)",
                      boxShadow: "0 6px 18px rgba(15, 77, 40, 0.06)",
                    }}
                  >
                    {/* Left: Image + Info */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flex: 1,
                      }}
                    >
                      <Box
                        component="img"
                        src={item.imageUrl}
                        alt={item.name}
                        sx={{
                          width: "70px",
                          height: "70px",
                          objectFit: "cover",
                          borderRadius: 2,
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          flex: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "clamp(1rem, 1vw, 1.5rem)",
                            fontWeight: "bold",
                            color: "#0f4d28",
                            fontFamily: "poppins",
                          }}
                        >
                          {sanitizeInput(item.name)}
                        </Typography>
                        {item.skipMealSelection && item.description && (
                          <Typography
                            sx={{
                              mt: 0.5,
                              fontFamily: "Tajawal",
                              color: "#444",
                              fontSize: "clamp(0.85rem, 2.5vw, 1rem)",
                            }}
                          >
                            {sanitizeInput(item.description)}
                          </Typography>
                        )}

                        {!cartItemShowsMealBreakdown(item) ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mt: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-evenly",
                                alignItems: "center",
                                border: "0.5px solid #aaa",
                                borderRadius: 3,
                                width: "100px",
                                maxHeight: "30px",
                                px: 1,
                              }}
                            >
                              <Button
                                sx={{ minWidth: 0, width: "35%" }}
                                onClick={() =>
                                  updateQty(item.id, Math.max(1, item.qty - 1))
                                }
                              >
                                -
                              </Button>
                              <Typography
                                sx={{
                                  fontSize: "1rem",
                                  textAlign: "center",
                                  width: "33%",
                                }}
                              >
                                {item.qty}
                              </Typography>
                              <Button
                                sx={{ minWidth: 0, width: "35%" }}
                                onClick={() => updateQty(item.id, item.qty + 1)}
                              >
                                +
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <Box>
                            {item.meals && item.meals.length > 0 && (
                              <Box sx={{ mt: 1, mb: 0 }}>
                                {item.meals.map((meal, idx) => (
                                  <li
                                    key={idx}
                                    style={{
                                      fontFamily: "Tajawal",
                                      color: "#222",
                                      fontSize: "clamp(0.8rem 1vw 1rem)",
                                    }}
                                  >
                                    {sanitizeInput(meal.name)} × {meal.qty}
                                  </li>
                                ))}
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Right: Pricing Info */}
                    <Box
                      sx={{
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        justifyContent: "space-between",
                        minWidth: "70px",
                        minHeight: "70px",
                      }}
                    >
                      {/* Top-right: DELETE + EDIT side by side */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          display: "flex",
                          gap: 0.5,
                        }}
                      >
                        <IconButton
                          onClick={() => removeFromCart(item.id)}
                          color="error"
                          sx={{
                            color: "#c62828",
                            "&:hover": { backgroundColor: "#f9d6d5" },
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>

                        {item.isCustom && (
                          <IconButton
                            onClick={() => {
                              setEditingItem(item);
                              if (products.length > 0) {
                                setOpenCustomMealPopup(true);
                              } else {
                                const waitForProducts = setInterval(() => {
                                  if (products.length > 0) {
                                    setOpenCustomMealPopup(true);
                                    clearInterval(waitForProducts);
                                  }
                                }, 100);
                              }
                            }}
                            sx={{
                              color: "#0f4d28",
                              "&:hover": { backgroundColor: "#eaf7ef" },
                            }}
                          >
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      {/* Bottom: Price info */}
                      <Box sx={{ mt: 5.5, textAlign: "right" }}>
                        {!cartItemShowsMealBreakdown(item) && (
                          <Typography
                            sx={{
                              fontSize: "0.85rem",
                              fontFamily: "Tajawal",
                              color: "text.secondary",
                            }}
                          >
                            ₪ {sanitizeInput(item.price.toString())} ×{" "}
                            {item.qty}
                          </Typography>
                        )}
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            fontFamily: "Tajawal",
                            fontSize: "0.95rem",
                            color: "#333",
                          }}
                        >
                          ₪ {sanitizeInput((item.price * item.qty).toString())}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
                {appliedDiscount !== null && (
                  <Box
                    sx={{
                      backgroundColor: "white",
                      borderRadius: 2,
                      mb: 2,
                      p: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                      overflow: "hidden",
                      maxHeight: showDiscount ? "200px" : "0px",
                      opacity: showDiscount ? 1 : 0,
                      transition:
                        "max-height 0.3s ease-in-out, opacity 0.3s ease-in-out",
                    }}
                  >
                    {/* Left: Image + Info */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flex: 1,
                      }}
                    >
                      <Box
                        component="img"
                        src="/free.png"
                        alt={appliedDiscount.description}
                        sx={{
                          width: "70px",
                          height: "70px",
                          objectFit: "cover",
                          borderRadius: 2,
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          flex: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "clamp(1rem, 1vw, 1.5rem)",
                            fontWeight: "bold",
                            color: "#0f4d28",
                            fontFamily: "poppins",
                          }}
                        >
                          {sanitizeInput(appliedDiscount.description)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Right: Pricing Info */}
                    <Box
                      sx={{
                        minWidth: "60px",
                        display: "flex",
                        flexDirection: "column",
                        pr: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontFamily: "Tajawal",
                          color: "#333",
                        }}
                      >
                        {calculateDiscountAmount()}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Checkout Button */}
          {cart.length > 0 && (
            <Box
              sx={{
                ml: "-16px",
                mr: "-16px",
                mt: "auto",
                borderTop: "1px solid rgba(27, 122, 61, 0.15)",
                display: "flex",
                background:
                  "linear-gradient(180deg, #eaf7ef 0%, #d8f0e0 100%)",
                flexDirection: "column",
                width: "calc(100% + 32px)",
                pt: 0.5,
                pb: 1.5,
              }}
            >
              <Box
                sx={{
                  borderRadius: 2,
                  p: 1,
                  pt: 2,
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    color: "#444",
                    p: 1,
                    pt: 0,
                    fontFamily: "Tajawal",
                  }}
                >
                  {calculateDiscountedTotal()}
                </Typography>

                <Typography
                  sx={{
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    color: "#444",
                    p: 1,
                    pt: 0,
                    fontFamily: "Tajawal",
                  }}
                >
                  {t("totalAmount")}
                </Typography>
              </Box>
              {errors.message && (
                <Typography
                  color="error"
                  align="center"
                  sx={{
                    mb: 2,
                    fontFamily: "Tajawal",
                  }}
                >
                  {errors.message}
                </Typography>
              )}

              <Button
                variant="contained"
                className="dc-btn-primary"
                sx={{
                  width: "90%",
                  fontSize: "1.15rem",
                  borderRadius: 3,
                  py: 1.4,
                  alignSelf: "center",
                }}
                onClick={() => {
                  if (validateMinItems())
                    window.location.href = "/OrderSummary";
                }}
              >
                {t("continueShopping")}
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>
      {openCustomMealPopup && products.length > 0 && (
        <CustomMealPopUp
          open={openCustomMealPopup}
          handleClose={() => setOpenCustomMealPopup(false)}
          existingItem={editingItem}
          isEditing
          onUpdate={(id, updatedItem) => {
            updateCustomMeal(id, updatedItem);
            setOpenCustomMealPopup(false);
          }}
          products={products}
        />
      )}
    </>
  );
};

export default MobileCart;
